// game/relics.ts — relic hook engine (DESIGN §6).
//
// A RelicDef is data: { hook: 'onGeneratorKilled', params: {...} }. The behaviour
// lives here as a registered hook key. Three kinds of key:
//
//   1. FIRED     — fireHook('onRoomClear', run) runs every held relic on that key.
//   2. MODIFIER  — modifier('statDamage', base) folds mul/add through held relics.
//   3. FLAG      — recorded into run.relicFlags for a *later zone* to read. Zone
//                  mechanics never import this module; they read the flag record
//                  off RunCtx-adjacent state, which is why flags are plain numbers.
//
// One-shot effects (max HP, dash charges, Gloom rate) are applied through
// recomputeRelicStats(), which is delta-based so it never stomps a shrine bonus
// and is safe to call again at every zone start.

import type { RelicDef } from '../types'
import { RELICS, RELICS_BY_ID, RELIC_RARITY_WEIGHT } from '../data/relics'
import { TUNING } from '../data/tuning'
import type { Rng } from '../engine/rng'
import { fx } from '../engine/fx'
import { emit } from '../engine/particles'
import type { Run } from './game'

export type HookEvent = Record<string, unknown>
export type RelicHook = (run: Run, params: Record<string, unknown>, ev: HookEvent) => void

const hooks = new Map<string, RelicHook>()
const held: RelicDef[] = []

// Deltas this module has already pushed into the player, so recompute is idempotent.
const applied = { maxHp: 0, dash: 0, gloomMul: 1 }

/** Last Ember charges left in the current zone. */
let lethalCharges = 0

export function registerRelicHook(key: string, fn: RelicHook): void {
  hooks.set(key, fn)
}

export function knownHooks(): string[] {
  return [...hooks.keys()]
}

export function heldRelics(): readonly RelicDef[] {
  return held
}

export function hasRelic(id: string): boolean {
  for (let i = 0; i < held.length; i++) if (held[i].id === id) return true
  return false
}

export function resetRelics(): void {
  held.length = 0
  applied.maxHp = 0
  applied.dash = 0
  applied.gloomMul = 1
  lethalCharges = 0
}

const noEvent: HookEvent = {}

/** Fire every held relic registered against `key`. Hot-path safe: no allocation. */
export function fireHook(key: string, run: Run, ev: HookEvent = noEvent): void {
  if (held.length === 0) return
  const fn = hooks.get(key)
  if (!fn) return
  for (let i = 0; i < held.length; i++) {
    const r = held[i]
    if (r.hook !== key) continue
    try {
      fn(run, (r.params ?? {}) as Record<string, unknown>, ev)
    } catch {
      /* a bad relic must not end the run */
    }
  }
}

/** Numeric modifier relics: fold mul/add through every matching hook. */
export function modifier(key: string, base: number): number {
  let v = base
  for (let i = 0; i < held.length; i++) {
    const r = held[i]
    if (r.hook !== key) continue
    const mul = r.params?.mul
    const add = r.params?.add
    if (typeof mul === 'number') v *= mul
    if (typeof add === 'number') v += add
  }
  return v
}

/** First matching relic's numeric param, or `dflt`. */
export function paramOf(key: string, name: string, dflt: number): number {
  for (let i = 0; i < held.length; i++) {
    const r = held[i]
    if (r.hook !== key) continue
    const v = r.params?.[name]
    if (typeof v === 'number') return v
  }
  return dflt
}

export function holdsHook(key: string): boolean {
  for (let i = 0; i < held.length; i++) if (held[i].hook === key) return true
  return false
}

// ---------------------------------------------------------------------------
// relic flags — the contract with zones 2-6
//
// Every flag below is a number on run.relicFlags. Zone mechanics read them; they
// never need to know which relic set them. 0/absent = off.
//
//   waterSpeedImmunity  — zone 2: water tiles do not slow the Warden
//   drySpeedBonus       — zone 2: speed multiplier while on dry stone
//   huskAutoSealOnWalk  — zone 3: walking over a downed husk seals it, no input
//   lampRadiusMul       — zone 4: multiplier on PlayerState.lampRadius
//   glintmothPassive    — zone 4: Glintmoths do not aggro on the Warden
//   cursedTreasureWeaken— zone 5: cursed-treasure marks apply Marked for N seconds
//   huskSealEmberBonus  — zone 3: extra embers per husk sealed
//   treasureSense       — any zone: hidden caches/vault doors ping on the map
// ---------------------------------------------------------------------------

function setFlags(run: Run, def: RelicDef): void {
  const p = def.params ?? {}
  const num = (k: string, d: number): number => (typeof p[k] === 'number' ? (p[k] as number) : d)
  switch (def.hook) {
    case 'tidewalker':
      run.relicFlags.waterSpeedImmunity = 1
      run.relicFlags.drySpeedBonus = num('dryMul', 1.1)
      break
    case 'huskSpade':
      run.relicFlags.huskAutoSealOnWalk = 1
      break
    case 'mothCloak':
      run.relicFlags.lampRadiusMul = num('lampMul', 1.3)
      run.relicFlags.glintmothPassive = 1
      break
    case 'appraiser':
      run.relicFlags.cursedTreasureWeaken = num('markSeconds', 5)
      break
    case 'huskSealEmbers':
      run.relicFlags.huskSealEmberBonus = (run.relicFlags.huskSealEmberBonus ?? 0) + num('add', 1)
      break
    case 'treasureSense':
      run.relicFlags.treasureSense = 1
      break
    default:
      break
  }
}

// ---------------------------------------------------------------------------
// grant + recompute

export function grantRelic(run: Run, def: RelicDef): void {
  held.push(def)
  setFlags(run, def)
  recomputeRelicStats(run)
  run.stats.relics.push(def.name)
  run.sfx('pickup')
  fx.floatText(def.name, run.player.pos.x, run.player.pos.y - 26, '#ffd98a', 13)
  emit(run.player.pos.x, run.player.pos.y, {
    count: 22,
    color: '#ffd98a',
    speed: 150,
    speedVar: 90,
    life: 0.6,
    lifeVar: 0.3,
    size: 4,
    shape: 1,
  })
  run.bark(`${def.name} — ${def.flavor}`)
}

/**
 * Push the one-shot stat relics into the player as *deltas*. Idempotent: call it
 * on grant and again after every player.reset() (zone start), and shrine bonuses
 * applied elsewhere survive untouched.
 */
export function recomputeRelicStats(run: Run): void {
  const p = run.player

  const wantHp = Math.round(modifier('statMaxHp', 0))
  const dHp = wantHp - applied.maxHp
  if (dHp !== 0) {
    p.maxHp += dHp
    p.hp = Math.max(1, Math.min(p.maxHp, p.hp + Math.max(0, dHp)))
    applied.maxHp = wantHp
  }

  const wantDash = Math.round(modifier('dashChargeAdd', 0))
  const dDash = wantDash - applied.dash
  if (dDash !== 0) {
    p.maxDashCharges = Math.max(1, p.maxDashCharges + dDash)
    while (p.dashCooldowns.length < p.maxDashCharges) p.dashCooldowns.push(0)
    p.dashCharges = Math.min(p.maxDashCharges, p.dashCharges + Math.max(0, dDash))
    applied.dash = wantDash
  }

  const wantGloom = modifier('gloomFillMul', 1)
  if (wantGloom !== applied.gloomMul) {
    run.gloomSys.rateMul = (run.gloomSys.rateMul / applied.gloomMul) * wantGloom
    applied.gloomMul = wantGloom
  }
}

/** Zone start: Last Ember recharges once per zone (DESIGN §6). */
export function onZoneStartRelics(run: Run): void {
  lethalCharges = holdsHook('lastEmber') ? Math.round(paramOf('lastEmber', 'perZone', 1)) : 0
  applied.gloomMul = 1 // gloom.reset() put rateMul back to 1
  recomputeRelicStats(run)
}

/**
 * Last Ember. Returns the damage the player should actually take: capped so the
 * blow leaves 1 HP, when a charge is available.
 */
export function absorbLethal(run: Run, amount: number): number {
  if (lethalCharges <= 0) return amount
  const p = run.player
  if (!p.alive || p.hp - amount > 0) return amount
  lethalCharges--
  run.shake(7, 0.5)
  run.hitstop(0.12)
  run.sfx('potion')
  run.bark('The last ember refuses. Once.')
  fx.floatText('LAST EMBER', p.pos.x, p.pos.y - 30, '#ffb347', 15)
  emit(p.pos.x, p.pos.y, { count: 30, color: '#ffb347', speed: 220, life: 0.7, lifeVar: 0.3, size: 4, shape: 1 })
  const reduced = Math.max(0, p.hp - 1)
  // At 1 HP the reduction is total, and player.hurt() early-returns on 0 damage —
  // which would leave no grace at all. Refusing death has to buy a breath.
  if (reduced <= 0) p.iframes = Math.max(p.iframes, TUNING.player.contactIframes * 2)
  return reduced
}

// ---------------------------------------------------------------------------
// derived read-outs used by the run (single call sites in game/)

/** Multiplier + flat bonus on all player-sourced damage. */
export function relicDamageMul(): number {
  return modifier('statDamage', 1)
}

/** Gravity of Gold: +1 damage per `perGold` held, capped. */
export function relicGoldDamage(gold: number): number {
  if (!holdsHook('goldGravity')) return 0
  const per = Math.max(1, paramOf('goldGravity', 'perGold', 100))
  const cap = paramOf('goldGravity', 'cap', 8)
  return Math.min(cap, Math.floor(gold / per))
}

export function relicGoldMagnetRadius(): number {
  return holdsHook('goldGravity') ? paramOf('goldGravity', 'radius', 190) : 0
}

export function relicSpeedMul(): number {
  return modifier('statSpeed', 1)
}

export function relicFoodHealMul(): number {
  return modifier('foodHealMul', 1)
}

export function relicEmberMul(): number {
  return modifier('emberBonus', 1)
}

export function relicPotionRadiusMul(): number {
  return modifier('potionRadius', 1)
}

export function relicSpecialCooldownMul(): number {
  return modifier('specialCooldownMul', 1)
}

export function relicPierceAdd(): number {
  return Math.round(modifier('pierceAdd', 0))
}

export function relicEliteEmberMul(): number {
  return modifier('eliteBounty', 1)
}

// ---------------------------------------------------------------------------
// offer draws

const drawScratch: RelicDef[] = []

/**
 * The in-run relic pool (DESIGN §6: "pool expandable via Provisioner stock
 * unlocks"). game/meta.ts sets this at run start from the save; null = the
 * whole table. RelicDef carries no tier field (types.ts is frozen), so the gate
 * is an id list living in data/unlocks.ts.
 */
let relicPool: Set<string> | null = null

export function setRelicPool(ids: readonly string[] | null): void {
  relicPool = ids && ids.length > 0 ? new Set(ids) : null
}

/**
 * `n` distinct relics the Warden does not already hold, weighted by rarity.
 * Deeper zones tilt the weight toward rare/epic (DESIGN §6: "bosses offer higher
 * tiers deeper"). Uses the run rng only — never Math.random.
 */
export function drawRelicOffers(rng: Rng, n: number, zone: number, out: RelicDef[]): RelicDef[] {
  out.length = 0
  drawScratch.length = 0
  for (let i = 0; i < RELICS.length; i++) {
    if (relicPool && !relicPool.has(RELICS[i].id)) continue
    if (!hasRelic(RELICS[i].id)) drawScratch.push(RELICS[i])
  }
  const depth = Math.max(0, zone - 1)
  for (let k = 0; k < n && drawScratch.length > 0; k++) {
    let total = 0
    for (let i = 0; i < drawScratch.length; i++) total += weightFor(drawScratch[i], depth)
    let roll = rng.next() * total
    let idx = drawScratch.length - 1
    for (let i = 0; i < drawScratch.length; i++) {
      roll -= weightFor(drawScratch[i], depth)
      if (roll <= 0) {
        idx = i
        break
      }
    }
    out.push(drawScratch[idx])
    drawScratch.splice(idx, 1)
  }
  return out
}

function weightFor(def: RelicDef, depth: number): number {
  const base = RELIC_RARITY_WEIGHT[def.rarity]
  if (def.rarity === 'common') return Math.max(8, base - depth * 7)
  if (def.rarity === 'rare') return base + depth * 2
  return base + depth * 5
}

export function relicById(id: string): RelicDef | undefined {
  return RELICS_BY_ID.get(id)
}

// ---------------------------------------------------------------------------
// hook implementations
//
// Modifier/flag keys are registered as no-ops so knownHooks() reports the whole
// vocabulary and a data typo in RelicDef.hook is visible at a glance.

const noop: RelicHook = () => {}

for (const key of [
  'statMaxHp',
  'statDamage',
  'statSpeed',
  'foodHealMul',
  'emberBonus',
  'potionRadius',
  'specialCooldownMul',
  'pierceAdd',
  'dashChargeAdd',
  'gloomFillMul',
  'goldGravity',
  'lastEmber',
  'eliteBounty',
  'tidewalker',
  'huskSpade',
  'mothCloak',
  'appraiser',
  'huskSealEmbers',
  'treasureSense',
]) {
  registerRelicHook(key, noop)
}

/** Ashfall — a dead generator takes the room with it. */
registerRelicHook('onGeneratorKilled', (run, params, ev) => {
  const x = typeof ev.x === 'number' ? ev.x : run.player.pos.x
  const y = typeof ev.y === 'number' ? ev.y : run.player.pos.y
  const damage = typeof params.damage === 'number' ? params.damage : 30
  const radius = typeof params.radius === 'number' ? params.radius : 120
  run.emberburst(x, y, radius, damage, false)
  run.shake(5, 0.3)
  run.bark('Ashfall.')
})

/** Quenchstone — the quiet after a room is worth something. */
registerRelicHook('onRoomClear', (run, params) => {
  const heal = typeof params.heal === 'number' ? params.heal : 10
  run.healPlayer(heal)
})

/** Coin-Reek — the dead were carrying their pay. */
registerRelicHook('goldOnKill', (run, params, ev) => {
  const chance = typeof params.chance === 'number' ? params.chance : 0.1
  if (!run.rngObj.chance(chance)) return
  const gold = typeof params.gold === 'number' ? params.gold : 5
  const x = typeof ev.x === 'number' ? ev.x : run.player.pos.x
  const y = typeof ev.y === 'number' ? ev.y : run.player.pos.y
  run.spawnPickupAt('gold', x, y, gold)
})

/** Glutton's Hearth — every meal leaves a little of itself behind. */
registerRelicHook('onFoodEaten', (run, params) => {
  const add = typeof params.maxHpAdd === 'number' ? params.maxHpAdd : 2
  run.player.maxHp += add
  run.player.hp = Math.min(run.player.maxHp, run.player.hp + add)
  fx.floatText(`+${add} MAX`, run.player.pos.x, run.player.pos.y - 34, '#8ee87a', 11)
})

/** Tinder Wake — the dash leaves the floor burning. */
registerRelicHook('dashBurnTrail', (run, params, ev) => {
  const x = typeof ev.x === 'number' ? ev.x : run.player.pos.x
  const y = typeof ev.y === 'number' ? ev.y : run.player.pos.y
  const dps = typeof params.dps === 'number' ? params.dps : 20
  const seconds = typeof params.seconds === 'number' ? params.seconds : 3
  const radius = typeof params.radius === 'number' ? params.radius : 24
  run.spawnHazard(x, y, radius, dps, seconds, '#ff8a2a')
})
